
import { mapState, mapMutations } from 'vuex';
import { hydrateWhenVisible, hydrateWhenIdle } from 'nuxt-lazy-hydration';
import { calculateStockAmount } from '~/utils/stock';

export default {
  name: 'ProductPage',

  components: {
    Block: hydrateWhenVisible(() => import('@/components/ui/block.vue')),
    Rating: hydrateWhenVisible(() => import('@/components/ui/rating.vue')),
    Swiper: hydrateWhenVisible(() => import('@/components/ui/swiper.vue')),
    ProductTags: hydrateWhenVisible(() =>
      import('@/components/shop/productTags.vue')
    ),
    Counter: hydrateWhenVisible(() => import('@/components/ui/counter.vue')),
    Reviews: hydrateWhenVisible(() => import('@/components/shop/reviews.vue')),
    Dropdown: hydrateWhenVisible(() => import('@/components/ui/dropdown.vue')),
    Product: hydrateWhenVisible(() => import('@/components/shop/product.vue')),
    Breadcrumbs: hydrateWhenIdle(() =>
      import('@/components/shop/breadcrumbs.vue')
    ),
    Skeleton: hydrateWhenIdle(() => import('@/components/ui/skeleton.vue')),
    WpImage: hydrateWhenIdle(() => import('@/components/ui/img.vue')),
    StockNotification: hydrateWhenIdle(() =>
      import('@/components/ui/stockNotification.vue')
    ),
    Favorite: hydrateWhenIdle(() => import('@/components/ui/favorite.vue')),
  },

  layout: 'product',

  async fetch() {
    const slug = this.$route.params.slug;
    const { data } = await this.$wc.products({ slug });
    if (data && data.length) {
      this.product = { ...data[0] };
      this.addProducts(data[0]);
      if (data[0]?.sku) {
        await this.getStockAmount(data[0].sku);
      }
    } else {
      console.log('error');
      return this.$nuxt.error({ statusCode: 404 });
    }
  },

  data() {
    return {
      product: null,
      dropdownIndex: null,
      relatedProducts: [],
      showText: false,
      listContent: '',
      textContent: '',
      hasScrolled: false,
      scrolledUp: false,
      lastCrollPosition: 0,
      showStockNotification: false,
      stockAmount: 0,
    };
  },

  computed: {
    ...mapState(['products', 'patientId', 'currency']),

    relatedIds() {
      const array = this.product
        ? [...this.crossAndUpsell, ...this.product.related_ids]
        : [];
      return [...new Set(array)].slice(0, 6);
    },

    brand() {
      return this.product?.attributes?.find((a) => a.name === 'brand')
        ? this.product.attributes.find((a) => a.name === 'brand').options[0]
        : 'Lev';
    },

    crossAndUpsell() {
      return this.product
        ? [...this.product.upsell_ids, ...this.product.cross_sell_ids].sort(
            () => (Math.random() > 0.5 ? 1 : -1)
          )
        : [];
    },

    cat() {
      return this.product.categories.length ? this.product.categories[0] : null;
    },

    rating() {
      return Math.round(parseFloat(this.product.average_rating));
    },

    quantity: {
      get() {
        return this.product
          ? this.$store.getters.getQuantity(this.product.sku)
          : 0;
      },
      set(value) {
        const quantity = parseInt(value);
        if (value > 0 && this.quantity > 0) {
          this.editCartItem({
            quantity,
            sku: this.product.sku,
          });
        } else if (value > 0) {
          this.insertToCart(quantity);
        } else {
          this.removeCartItem(this.product);
        }
      },
    },

    price() {
      return this.$store.getters.getActualPrice(this.product.sku);
    },

    originalPrice() {
      return this.$store.getters.getPrice(this.product.sku);
    },

    onSale() {
      return (
        this.product &&
        this.originalPrice &&
        this.price &&
        this.originalPrice > this.price
      );
    },
  },

  watch: {
    relatedIds(value) {
      if (value.length) {
        this.getRelatedProducts();
      }
    },
    product(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.extractUlAndRestContent();
      }
    },
  },

  mounted() {
    if (this.user && this.myFavorites && this.myFavorites.length) {
      const myFavoritesIds = this.myFavorites.map((f) => f.sage_id);
      this.isFavorite = myFavoritesIds.includes(this.product.sku);
    }

    this.localQuantity = this.quantity;
    setTimeout(() => {
      if (this.$refs && this.$refs.text && this.$refs.clientHeight < 350) {
        this.showText = true;
      }
    }, 2000);

    window.addEventListener('scroll', this.handleScroll);

    this.$bolev.metaEvent({
      type: 'contentview',
      patientId: this.patientId,
      sku: this.product?.sku,
      price: this.$store.getters.getActualPrice(this.product?.sku),
    });
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },

  methods: {
    ...mapMutations([
      'addProducts',
      'addToCart',
      'editCartItem',
      'removeCartItem',
      'setProducts',
      'openLoginModal',
    ]),

    async getStockAmount(sku) {
      try {
        const { data } = await this.$bolev.getProductStock({
          sku,
          patientId: this.patientId,
        });
        this.stockAmount = calculateStockAmount(data);
      } catch (_) {
        this.errorMessage = 'Something went wrong';
      }
    },

    async getRelatedProducts() {
      const { data } = await this.$wc.products({
        ids: this.relatedIds,
      });
      this.relatedProducts = [...data];
      this.setProducts(data);
    },

    closeStockNotification() {
      this.showStockNotification = false;
    },

    openCloseDropdowns(index) {
      if (this.dropdownIndex === index) {
        this.dropdownIndex = null;
      } else {
        this.dropdownIndex = index;
      }
    },

    addOne() {
      if (this.stockAmount > this.quantity) {
        if (this.quantity === 0) {
          this.insertToCart(1);
        } else {
          this.editCartItem({
            quantity: this.quantity + 1,
            sku: this.product.sku,
          });
        }
      }
    },

    removeOne() {
      if (this.quantity < 1) return;
      if (this.quantity === 1) {
        this.removeCartItem(this.product);
      } else {
        this.editCartItem({
          quantity: this.quantity - 1,
          sku: this.product.sku,
        });
      }
    },

    insertToCart(q) {
      if (this.quantity === 0) {
        this.addToCart({
          quantity: q,
          sku: this.product.sku,
          regularPrice: this.$store.getters.getPrice(this.product.sku),
          price: this.$store.getters.getActualPrice(this.product.sku),
          onSale: this.product.on_sale,
          salePrice: this.product.sale_price,
          categories: this.product.categories.map((c) => c.id),
        });
        this.$bolev.metaEvent({
          type: 'addToCart',
          patientId: this.patientId,
          quantity: q,
          sku: this.product?.sku,
          price: this.$store.getters.getActualPrice(this.product.sku),
        });
      }
    },

    extractUlAndRestContent() {
      if (this.product && this.product.description) {
        const ulRegex = /<ul>[\s\S]*?<\/ul>/;
        const ulMatch = this.product.description.match(ulRegex);

        if (ulMatch) {
          this.listContent = ulMatch[0];
          this.textContent = this.product.description.replace(ulRegex, '');
        } else {
          this.textContent = this.product.description;
        }
      }
    },

    handleScroll() {
      const currentScrollPosition = window.scrollY;
      const threshold = 420;

      if (currentScrollPosition < this.lastScrollPosition) {
        this.scrolledUp = true;
      } else {
        this.scrolledUp = false;
      }

      if (currentScrollPosition > threshold && !this.hasScrolled) {
        this.hasScrolled = true;
      } else if (currentScrollPosition <= threshold && this.hasScrolled) {
        this.hasScrolled = false;
      }

      this.lastScrollPosition = currentScrollPosition;
    },
  },

  head() {
    if (this.product) {
      const productMeta = this.$seo.product({
        sku: this.product.sku,
        price: this.$store.getters.getActualPrice(this.product.sku),
        stock: this.stockAmount > 0 ? 'in stock' : 'out of stock',
        brand: this.brand,
      });

      const yoastMeta = this.$seo.yoast(this.product.yoast_head_json);
      yoastMeta.meta = [...yoastMeta.meta, ...productMeta];

      const gData = this.$gdata.product({
        brand: this.brand,
        price: this.$store.getters.getActualPrice(this.product.sku),
        inStock: this.stockAmount > 0,
        product: this.product,
        currency: this.$seo.currency(),
        url: this.$seo.path(),
      });

      return { ...yoastMeta, ...gData };
    }
  },
};
